import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceMetrics = ({ partnerData }) => {
  const performanceData = [
    {
      label: 'Total Bookings',
      value: '1,247',
      change: '+12.5%',
      trend: 'up',
      icon: 'Calendar',
      color: 'primary'
    },
    {
      label: 'Customer Satisfaction',
      value: '4.8/5.0',
      change: '+0.3',
      trend: 'up',
      icon: 'Star',
      color: 'success'
    },
    {
      label: 'Compliance Rating',
      value: '98%',
      change: '+2%',
      trend: 'up',
      icon: 'Shield',
      color: 'success'
    },
    {
      label: 'Response Time',
      value: '2.4h',
      change: '-0.5h',
      trend: 'up',
      icon: 'Clock',
      color: 'warning'
    },
    {
      label: 'Revenue Generated',
      value: '$124,500',
      change: '+18.2%',
      trend: 'up',
      icon: 'DollarSign',
      color: 'success'
    },
    {
      label: 'Active Contracts',
      value: '23',
      change: '+5',
      trend: 'up',
      icon: 'FileText',
      color: 'secondary'
    }
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'booking',
      description: 'New booking completed for London trip',
      timestamp: '2 hours ago',
      icon: 'CheckCircle',
      color: 'success'
    },
    {
      id: 2,
      type: 'review',
      description: 'Received 5-star review from client',
      timestamp: '5 hours ago',
      icon: 'Star',
      color: 'warning'
    },
    {
      id: 3,
      type: 'compliance',
      description: 'Compliance audit passed successfully',
      timestamp: '1 day ago',
      icon: 'Shield',
      color: 'success'
    },
    {
      id: 4,
      type: 'payment',
      description: 'Payment received: $4,500',
      timestamp: '2 days ago',
      icon: 'DollarSign',
      color: 'primary'
    }
  ];

  const getColorClass = (color) => {
    switch (color) {
      case 'success': return 'text-success bg-success/10 border-success/20';
      case 'warning': return 'text-warning bg-warning/10 border-warning/20';
      case 'primary': return 'text-primary bg-primary/10 border-primary/20';
      case 'secondary': return 'text-secondary bg-secondary/10 border-secondary/20';
      default: return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Performance Overview */}
      <div className="glass-container rounded-xl border border-border/50 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Icon name="TrendingUp" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Performance Metrics</h2>
              <p className="text-sm text-muted-foreground">Your partnership performance overview</p>
            </div>
          </div>
          <Button variant="outline" iconName="Download" size="sm">
            Export Report
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {performanceData?.map((metric, index) => (
            <div
              key={index}
              className="p-4 rounded-lg border border-border/50 hover:bg-accent/5 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${getColorClass(metric?.color)}`}>
                  <Icon name={metric?.icon} size={20} />
                </div>
                <div className="flex items-center space-x-1">
                  <Icon
                    name={metric?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'}
                    size={14}
                    className={metric?.trend === 'up' ? 'text-success' : 'text-error'}
                  />
                  <span className={`text-xs font-medium ${metric?.trend === 'up' ? 'text-success' : 'text-error'}`}>
                    {metric?.change}
                  </span>
                </div>
              </div>
              <p className="text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
              <p className="text-sm text-muted-foreground">{metric?.label}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Activity */}
      <div className="glass-container rounded-xl border border-border/50 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center">
            <Icon name="Activity" size={24} className="text-secondary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Recent Activity</h2>
            <p className="text-sm text-muted-foreground">Latest updates and achievements</p>
          </div>
        </div>

        <div className="space-y-3">
          {recentActivity?.map((activity) => (
            <div
              key={activity?.id}
              className="flex items-start space-x-4 p-4 rounded-lg border border-border/50 hover:bg-accent/5 transition-colors"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border flex-shrink-0 ${getColorClass(activity?.color)}`}>
                <Icon name={activity?.icon} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{activity?.description}</p>
                <p className="text-xs text-muted-foreground mt-1">{activity?.timestamp}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Performance Goals */}
      <div className="glass-container rounded-xl border border-border/50 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
            <Icon name="Target" size={24} className="text-accent" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Performance Goals</h2>
            <p className="text-sm text-muted-foreground">Track your progress towards targets</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Monthly Booking Target</span>
              <span className="text-sm font-semibold text-foreground">85/100</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: '85%' }} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Customer Satisfaction</span>
              <span className="text-sm font-semibold text-foreground">96/100</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-success rounded-full" style={{ width: '96%' }} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Compliance Score</span>
              <span className="text-sm font-semibold text-foreground">98/100</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-success rounded-full" style={{ width: '98%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;